"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Recorded, Cancelled" },
  { from: "Recorded", to: "Draft, Cancelled" },
  {
    from: "Converted",
    to: "Cancelled only — blocked if a Bill was created from this GRPO",
  },
  {
    from: "Cancelled",
    to: "Draft, Recorded — blocked if this GRPO had added stock; stock was released on cancel",
  },
];

const CONVERT_FROM = [
  {
    from: "Purchase Order",
    when: "Accepted or Partially Recorded. Convert To GRPO on the PO, or CONVERT on the PO list",
    result:
      "Vendor, location, remaining quantity come across. Saving this GRPO moves the PO to Partially Recorded, or Converted if remaining hits zero",
  },
  {
    from: "Scratch",
    when: "Add with nothing pre-filled",
    result: "A new GRPO. Save always makes Draft — there is no Save & Recorded",
  },
  {
    from: "Duplicate",
    when: "Duplicate Document on the list or detail",
    result:
      "Opens Add with the same vendor, items, and amounts. New GRPO number, today's dates, Draft, no PO link, no attachments",
  },
];

const CONVERT_TO = [
  {
    to: "Bill",
    when: "Recorded only",
    where:
      "Convert To Bill on the detail, To Bills on the row, or CONVERT → Convert to Bills on the list",
    result:
      "Opens a new Bill with this GRPO linked. The GRPO becomes Converted when that bill is saved",
  },
];

const RULES = [
  {
    topic: "Ledger only when stock was added",
    rule: "If this GRPO created batches, it posts Dr inventory / Cr Goods Received Not Invoiced (GRNI), including Draft. No batches means no journal (Stock Add Source = Bill, or a customer drop-ship address). Cancelled deletes that journal. Billing later settles GRNI — it does not re-post this GRPO",
  },
  {
    topic: "Plan add-on",
    rule: "GRPO is a plan feature. Creating is gated by that add-on. Editing, Mark As, converting to Bill, duplicating, and deleting are not separately gated",
  },
  {
    topic: "No primary-bank gate",
    rule: "You can create a GRPO without a primary bank",
  },
  {
    topic: "GRPO number",
    rule: "Auto-generated with a GRPO prefix unless you turn that off under GRPO Configuration on the form. That same drawer stores Terms & Conditions. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Vendor",
    rule: "All four are required, plus shipping address and at least one item. After save they are locked. Converted-from a PO also locks those fields before save",
  },
  {
    topic: "Stock Add Source is a company setting",
    rule: "Settings → Inventory Config → Purchase Flow Stock Add Source. Set it to GRPO for this module to add warehouse quantity. GRPO appears on that list only when the add-on is on your plan. The choice is stored on each GRPO at create time",
  },
  {
    topic: "Stock add on this GRPO",
    rule: "Stock Add Source = GRPO adds quantity on save, including Draft, when shipping is Organization (no customer). Customer / drop-ship forces stock off. Stock Add Source = Bill means this GRPO does not add stock — convert to Bill still works. Inventory Impact on detail shows the snapshot from create time",
  },
  {
    topic: "Cancelled with stock add cannot be reverted",
    rule: "Mark As still offers Draft and Recorded on Cancelled, but the save is rejected if this GRPO had added stock",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Recorded, Converted, and Cancelled cannot be edited",
  },
  {
    topic: "Convert to Bill only from Recorded",
    rule: "Draft, Converted, and Cancelled cannot convert. There is no convert to Debit Note and no Rejection status on GRPO",
  },
  {
    topic: "Cancel and delete",
    rule: "Blocked if a Bill was created from this GRPO. With stock add and no customer, cancel or delete reverses warehouse quantity and recalculates linked PO remaining quantity",
  },
  {
    topic: "Credit limit and overdue",
    rule: "Checked on create if GRPO is in this vendor's Restriction Document / Restriction Payment Terms Document",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a GRPO" },
      { heading: "2. Stock add — Inventory Config" },
      { heading: "3. Viewing, searching, and exporting" },
      { heading: "4. Editing a GRPO" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Changing status" },
      { heading: "7. Converted from — Purchase Order" },
      { heading: "8. Converted to — Bill" },
      { heading: "9. Duplicating a GRPO" },
      { heading: "10. Deleting a GRPO" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function GrpoPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            GRPO
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording that goods from a purchase order (or a
            direct receipt) have arrived
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>GRPO</B> (Goods Received Purchase Order) is the receipt.{" "}
            <B>Save</B> always creates <B>Draft</B> — there is no Save
            &amp; Recorded. <B>Mark As Recorded</B> is when you can{" "}
            <B>Convert To Bill</B>. Whether warehouse quantity is added
            here, or later on the Bill, is{" "}
            <B>Settings → Inventory Config → Stock Add Source</B>. Convert
            to Bill stays either way. This module does not convert to a
            Debit Note.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a GRPO
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick Division, Branch, Warehouse. Only <B>Save</B> — that
            persists as <B>Draft</B>. Backend hardcodes Draft on create.
          </p>
          <Definitions
            items={[
              {
                term: "GRPO number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>GRPO Configuration</B> on the form. Default prefix{" "}
                    <B>GRPO</B>. Terms &amp; Conditions live in that drawer.
                    Locked after save.
                  </>
                ),
              },
              {
                term: "Vendor and shipping",
                desc: (
                  <>
                    Vendor required. Shipping Organization or Customer. A
                    customer address is drop-ship — when Stock Add Source is
                    GRPO, that forces stock add off.
                  </>
                ),
              },
              {
                term: "Batches",
                desc: (
                  <>
                    When Stock Add Source is GRPO, lines show batch number,
                    quantity, purchase price, optional vendor batch /
                    expiry. Return cost later uses that purchase price, not
                    a selling price.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add GRPO with GRPO Configuration and batch columns when Stock Add Source is GRPO." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Stock add — Inventory Config
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Company setting, not a toggle you invent per document. Point the
            catalogue to Settings → Inventory Config. What this screen uses:
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Stock Add Source
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    This GRPO
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    The Bill later
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827]">
                    GRPO
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Adds stock on save (Organization address, no customer)
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Does not add stock. Settles GRNI
                  </td>
                </tr>
                <tr>
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827]">
                    Bill
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Does not add stock
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Adds stock when you save the bill
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Callout type="note">
            Convert PO → GRPO → Bill is unchanged by this setting. Only
            which document moves warehouse quantity changes. Sales Stock
            Deduct Source (Invoice vs Delivery Challan) is a different
            switch.
          </Callout>
          <Screenshot caption="Screenshot: Inventory Impact on GRPO detail showing Stock Add Enabled or Disabled." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Status tabs Draft / Recorded / Converted / Cancelled. Toolbar:{" "}
            <B>Add</B>; CONVERT → Convert to Bills; <B>Export GRPO</B>;
            Preferences; Manage Custom Fields, Refresh, Reset Columns Width.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: View Vendor, Download, Print, Stock Movement tab,
            Converted From Purchase Order badge, Converted To Bills.
          </p>
          <Screenshot caption="Screenshot: GRPO list with CONVERT to Bills and Export GRPO." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing a GRPO
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only. Vendor and location stay locked.
          </p>

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Detail: <B>Download</B> and <B>Print</B>.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Button: <B>Mark GRPO As</B>. Converted is normally set when a
            bill is saved from this GRPO.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[180px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Mark GRPO As on a Draft — Recorded and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Converted from — Purchase Order
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_FROM.map((r, i) => (
                  <tr
                    key={r.from}
                    className={
                      i < CONVERT_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Converted to — Bill
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_TO.map((r, i) => (
                  <tr
                    key={r.to}
                    className={
                      i < CONVERT_TO.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                      {r.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Convert To Bill on a Recorded GRPO." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Duplicating a GRPO
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            <B>Duplicate Document</B>. New number, Draft, no Purchase Order
            link — it is a fresh receipt, not another receive of the same
            PO.
          </p>

          <h3
            id="section-0-sub-9"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            10. Deleting a GRPO
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Blocked if a Bill was created from this GRPO. Otherwise delete
            (or cancel) reverses stock when this GRPO had added it, and
            puts remaining quantity back on the Purchase Order.
          </p>
          <Screenshot caption="Screenshot: delete blocked because a bill was created from this GRPO." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/purchase/purchase-orders"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Purchase Orders <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Accepted or Partially Recorded POs convert into this GRPO
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/bills"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Bills <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Recorded GRPO converts only into a Bill
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/inventory-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Inventory Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Stock Add Source — GRPO or Bill
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
